<template>
  <Dialog
      :header="t('ADD_NEW_INSTITUTION')"
      v-model:visible="visible"
      modal
      class="institution-modal"
      :draggable="false"
      :dismissable-mask="true"
      @hide="onHide"
  >
    <div class="mb-2">
      <label for="id-business-name" class="form-label">{{ t('INSTITUTION_NAME') }}</label>
      <InputText
          id="id-business-name"
          v-model="modalData.businessName"
          type="text"
          class="w-100"
          :invalid="validationPending && !modalData.businessName"
      />
    </div>

    <div class="mb-2">
      <label for="id-registry-code" class="form-label">{{ t('REGISTRATION_NUMBER') }}</label>
      <InputText
          id="id-registry-code"
          v-model="modalData.registryCode"
          type="text"
          maxlength="8"
          class="w-100"
          :invalid="validationPending && !validateRegistryCode(modalData.registryCode)"
      />
    </div>

    <template #footer>
      <Button :label="t('CANCEL')" severity="secondary" @click="visible = false"/>
      <Button :label="t('SAVE')" @click="addInstitution"/>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import {computed, ref} from "vue";
import type {InstitutionDto} from "@/model/institution";
import {useI18n} from "vue-i18n";
import {validateRegistryCode} from "@/util/validationUtils";
import {useToast} from "@/util/toastUtils";
import {injectAxios} from "@/util/inject";

const {t} = useI18n();
const toast = useToast();
const axios = injectAxios();

const modalData = ref<Partial<InstitutionDto>>({});
const validationPending = ref(false);
const visible = ref(false);
const referenceId = ref("");

const emit = defineEmits<{
  afterSave: []
}>()

const isFormValid = computed(() => {
  return !!modalData.value && !!modalData.value.businessName && validateRegistryCode(modalData.value.registryCode);
});

const onHide = () => {
  visible.value = false;
};

const addInstitution = async (ev: Event) => {
  ev.preventDefault();

  if (!isFormValid.value) {
    validationPending.value = true;
    toast.makeErrorToast(t("ERRORS.FORM_INVALID"));
    return;
  }

  validationPending.value = false;
  await axios.post(`/institutions`, modalData.value);
  visible.value = false;
  toast.makeSuccessToast(t("OPERATION_SUCCESSFUL"));
  emit("afterSave");
};

const showCreationModal = () => {
  referenceId.value = "";
  modalData.value = {};
  validationPending.value = false;
  visible.value = true;
};

defineExpose({
  showCreationModal,
});
</script>

<style scoped>
.institution-modal :deep(.p-dialog) {
  width: 28rem;
}
</style>
